import { useMemo } from 'react';
import { useTheme } from '@course-authoring/shared-types';
import type { ImageInsertData, ExistingImageData } from './imageInsertTypes';
import { useImageInsertModalController } from './useImageInsertModalController';
import { ExistingImagePreview } from './ExistingImagePreview';
import { ImageSelectionView } from './ImageSelectionView';
import { AltTextStep } from './AltTextStep';
import { AIEditPanel } from './AIEditPanel';
import { ModalShell } from './ModalShell';

export type { ImageInsertData, ExistingImageData } from './imageInsertTypes';

interface ImageInsertModalProps {
  isOpen: boolean;
  onClose: () => void;
  onInsert: (data: ImageInsertData) => void;
  targetAspectRatio?: string;
  initialImage?: ExistingImageData;
}

export function ImageInsertModal({
  isOpen,
  onClose,
  onInsert,
  targetAspectRatio,
  initialImage,
}: ImageInsertModalProps) {
  const { tokens } = useTheme();
  const styles = useMemo(() => createStyles(tokens), [tokens]);

  const controller = useImageInsertModalController({
    isOpen,
    onInsert,
    onClose,
    targetAspectRatio,
    initialImage,
  });

  const { state } = controller;
  const title = controller.getTitle();

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      ariaLabelledBy="image-modal-title"
      width="720px"
      borderRadius="8px"
    >
        <div style={styles.header}>
          <h3 id="image-modal-title" style={styles.title}>
            {title}
          </h3>
          <button
            style={styles.closeButton}
            onClick={onClose}
            type="button"
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        <div style={styles.body}>
          {state.screen === 'existing-image' && initialImage && (
            <ExistingImagePreview
              image={initialImage}
              onReplace={controller.handleReplace}
              onEditWithAI={controller.handleEditWithAI}
              onRemoveImage={controller.handleRemoveImage}
              onInsert={controller.handleInsert}
              onClose={onClose}
            />
          )}

          {state.screen === 'image-selection' && (
            <ImageSelectionView
              activeTab={state.activeTab}
              onTabChange={controller.handleTabChange}
              onImageReady={controller.handleImageReady}
              uploadState={state.uploadState}
              onUploadStateChange={controller.handleUploadStateChange}
              aiGenerateState={state.aiGenerateState}
              onAIGenerateStateChange={controller.handleAIGenerateStateChange}
              onGenerate={controller.handleGenerate}
              libraryState={state.libraryState}
              onLibraryStateChange={controller.handleLibraryStateChange}
              targetAspectRatio={targetAspectRatio}
              pendingRemoval={state.pendingRemoval}
              onConfirmRemoval={controller.handleConfirmRemoval}
              onClose={onClose}
            />
          )}

          {state.screen === 'alt-text-step' && state.selectedImage && (
            <AltTextStep
              selectedImage={state.selectedImage}
              scopeAccountId={controller.scopeAccountId}
              scopeAccountName={controller.scopeAccountName}
              onInsert={controller.handleInsert}
              onBack={controller.handleBack}
              onClose={onClose}
            />
          )}

          {state.screen === 'ai-edit' && initialImage && (
            <AIEditPanel
              sourceImageUrl={initialImage.src}
              sourceImageAlt={initialImage.alt || 'Current image'}
              state={state.aiEditState}
              onStateChange={controller.handleAIEditStateChange}
              onEdit={controller.handleAIEdit}
              onImageReady={controller.handleImageReady}
              onBack={controller.handleBack}
              targetAspectRatio={targetAspectRatio}
            />
          )}
        </div>
    </ModalShell>
  );
}

function createStyles(tokens: ReturnType<typeof useTheme>['tokens']): Record<string, React.CSSProperties> {
  return {
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '16px 20px',
      borderBottom: `1px solid ${tokens.text.subtle}`,
      flexShrink: 0,
    },
    title: {
      margin: 0,
      fontSize: '18px',
      fontWeight: 600,
      color: tokens.text.primary,
    },
    closeButton: {
      background: 'none',
      border: 'none',
      fontSize: '24px',
      cursor: 'pointer',
      color: tokens.text.muted,
      padding: '0 4px',
      lineHeight: 1,
      minWidth: '44px',
      minHeight: '44px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    body: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      overflow: 'hidden',
    },
  };
}
